import { Component, OnInit, Input, OnChanges } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { Store } from '@ngrx/store';

// Redux
import { setTitle, navigate } from 'src/app/actions/core.actions';
import { setCommonIdBrowserTitle } from 'src/app/actions/common-id/common-id-core.actions';
import { RootState } from 'src/app/reducers';

// models
import { Subject } from 'src/app/models/common-data';
import { CommonIdPlaylistSearchCondition } from 'src/app/models/common-id/common-id-playlist-search-condition';

// config
import { PLAYLISTS_SUBJECT_ALL_ID, PLAYLISTS_SUBJECT_ALL_NAME } from 'src/app/resources/config';
import { CommonIdPlaylistSortType, CommonIdPlaylistSortTypeDisplayName } from 'src/app/resources/common-id-config';
import { RoutingPathResolver } from 'src/app/app-routing-path-resolver';

// mapper
import { CommonIdPlaylistSearchQueryParamsMapper } from './../../../../mappers/common-id/common-id-playlist-search-query-params-mapper';

interface SortOption {
  type: CommonIdPlaylistSortType;
  displayName: CommonIdPlaylistSortTypeDisplayName;
}

@Component({
  selector: 'app-common-id-playlists-search-condition',
  templateUrl: './playlists-search-condition.component.html',
  styleUrls: ['./playlists-search-condition.component.scss']
})
export class CommonIdPlaylistsSearchConditionComponent implements OnInit, OnChanges {
  private LOG_SOURCE = this.constructor.name;
  private title = 'おすすめ問題検索条件欄';

  matIcon: string;
  contentTitle: string;
  subjectOption: Subject[] = [];
  playlistForm: UntypedFormGroup;

  defaultSortOption: SortOption;
  sortOptions: SortOption[] = [];

  isFreeComesFirstChecked = true;

  @Input() isPremiumUser: boolean;
  @Input() subjects: Subject[];
  @Input() selectedSearchCondition: CommonIdPlaylistSearchCondition;

  constructor(private store: Store<RootState>) {}

  ngOnInit() {
    this.store.dispatch(setCommonIdBrowserTitle({ subTitle: this.title }));
    setTimeout(() => this.store.dispatch(setTitle({ title: this.title })));
  }

  ngOnChanges() {
    this.setUpForms();
    this.setUpIconAndTitle();
    this.setUpSubjectOption();
    this.setUpSortOptions();
    this.setUpIsFreeComesFirstChecked();
  }

  onChangeSubject() {
    this.selectedSearchCondition.subjectId = this.playlistForm.get('subjectId').value;
    this.addQueryParam();
  }

  onChangeSort() {
    const sortOptionValue = this.playlistForm.get('sort').value;
    this.selectedSearchCondition.sortType = CommonIdPlaylistSortType[sortOptionValue];
    this.addQueryParam();
  }

  onChangeFreeComesFirst(change: MatCheckboxChange) {
    this.isFreeComesFirstChecked = change.checked;
    if (this.isFreeComesFirstChecked) {
      this.selectedSearchCondition.free = true;
    } else {
      delete this.selectedSearchCondition.free;
    }

    this.addQueryParam();
  }

  private setUpForms() {
    this.playlistForm = new UntypedFormGroup({
      subjectId: new UntypedFormControl(),
      sort: new UntypedFormControl()
    });
  }

  private setUpIconAndTitle() {
    if (this.selectedSearchCondition.tag) {
      this.matIcon = 'tag';
      this.contentTitle = this.selectedSearchCondition.tag;
    } else {
      this.matIcon = 'view_module';
      this.contentTitle = 'おすすめ問題セット';
    }
  }

  private setUpSubjectOption() {
    // 全ての科目の選択肢を追加する
    const allSubjectOption: Subject[] = [{ id: PLAYLISTS_SUBJECT_ALL_ID, name: PLAYLISTS_SUBJECT_ALL_NAME }];
    this.subjectOption = allSubjectOption.concat(this.subjects);

    if (this.selectedSearchCondition && this.selectedSearchCondition.subjectId) {
      this.playlistForm.get('subjectId').setValue(this.selectedSearchCondition.subjectId);
    } else {
      this.playlistForm.get('subjectId').setValue(PLAYLISTS_SUBJECT_ALL_ID);
    }
  }

  private setUpSortOptions() {
    this.defaultSortOption = {
      type: CommonIdPlaylistSortType.PUBLISHED_DATE,
      displayName: CommonIdPlaylistSortTypeDisplayName.PUBLISHED_DATE
    };
    this.sortOptions = [
      this.defaultSortOption,
      { type: CommonIdPlaylistSortType.PLAYLIST_ID_ASC, displayName: CommonIdPlaylistSortTypeDisplayName.PLAYLIST_ID_ASC },
      { type: CommonIdPlaylistSortType.PLAYLIST_ID_DESC, displayName: CommonIdPlaylistSortTypeDisplayName.PLAYLIST_ID_DESC },
      { type: CommonIdPlaylistSortType.DURATION, displayName: CommonIdPlaylistSortTypeDisplayName.DURATION }
    ];

    if (this.selectedSearchCondition && this.selectedSearchCondition.sortType) {
      this.playlistForm.get('sort').setValue(this.selectedSearchCondition.sortType);
    } else {
      this.selectedSearchCondition.sortType = this.defaultSortOption.type;
      this.playlistForm.get('sort').setValue(this.defaultSortOption.type);
    }
  }

  private setUpIsFreeComesFirstChecked() {
    if (this.selectedSearchCondition && this.selectedSearchCondition.free) {
      this.isFreeComesFirstChecked = true;
    } else {
      this.isFreeComesFirstChecked = false;
    }
  }

  private addQueryParam() {
    let condition: CommonIdPlaylistSearchCondition = {
      subjectId: this.selectedSearchCondition.subjectId,
      sortType: this.selectedSearchCondition.sortType
    };

    // 無料会員の場合のみ無料優先フラグの設定を行う
    if (!this.isPremiumUser) {
      if (this.isFreeComesFirstChecked) {
        condition = { ...condition, free: this.isFreeComesFirstChecked };
      } else {
        delete condition.free;
      }
    }

    if (this.selectedSearchCondition.tag) condition = { ...condition, tag: this.selectedSearchCondition.tag };

    const queryParams = CommonIdPlaylistSearchQueryParamsMapper.encodeCommonIdPlaylistSearchCondition(condition);

    this.store.dispatch(navigate({ url: RoutingPathResolver.resolveCommonIdPlaylists(), extras: { queryParams } }));
  }
}
